import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { Link, Navigate } from 'react-router-dom';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title } from 'chart.js';
import BreadCrumb from '../BreadCrumb';
import { useAuth } from '../Auth/Auth';
import MoonLoader from 'react-spinners/MoonLoader';
import { formatCurrency } from '../Help/helper';
import {
  ChartPieIcon,
  CurrencyDollarIcon,
  WrenchScrewdriverIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';
import { toast, ToastContainer } from 'react-toastify';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title);

const pages = [
  { name: 'Reports', to: '/analytics', component: Link, current: false },
  { name: 'Revenue by Service', to: '/analytics/revenue', component: Link, current: true },
];

const RevenueByServiceType = () => {
  const { role, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [serviceRevenue, setServiceRevenue] = useState({});
  const [partsRevenue, setPartsRevenue] = useState({});
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [servicesTotal, setServicesTotal] = useState(0);
  const [partsTotal, setPartsTotal] = useState(0);
  const [topServices, setTopServices] = useState([]);
  const [topParts, setTopParts] = useState([]);

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const fetchRevenueData = async () => {
    setIsLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'newInvoices'));
      const serviceMap = {};
      const partMap = {};
      let servicesSum = 0;
      let partsSum = 0;

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.cartItems && Array.isArray(data.cartItems)) {
          data.cartItems.forEach(item => {
            const itemTotal = (item.price || 0) * (item.quantity || 0);
            const itemType = item.type || 'part'; // Default to part if not specified

            if (itemType === 'service') {
              if (!serviceMap[item.description]) {
                serviceMap[item.description] = { revenue: 0, count: 0 };
              }
              serviceMap[item.description].revenue += itemTotal;
              serviceMap[item.description].count += item.quantity || 0;
              servicesSum += itemTotal;
            } else {
              if (!partMap[item.description]) {
                partMap[item.description] = { revenue: 0, count: 0 };
              }
              partMap[item.description].revenue += itemTotal;
              partMap[item.description].count += item.quantity || 0;
              partsSum += itemTotal;
            }
          });
        }
      });

      // Sort and get top 10
      const sortedServices = Object.entries(serviceMap)
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);

      const sortedParts = Object.entries(partMap)
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);

      setServiceRevenue(serviceMap);
      setPartsRevenue(partMap);
      setServicesTotal(servicesSum);
      setPartsTotal(partsSum);
      setTotalRevenue(servicesSum + partsSum);
      setTopServices(sortedServices);
      setTopParts(sortedParts);
    } catch (error) {
      logger.error('Error fetching revenue data:', error);
      toast.error('Failed to load revenue data');
    } finally {
      setIsLoading(false);
    }
  };

  // Chart Data
  const serviceVsPartsData = {
    labels: ['Services', 'Parts'],
    datasets: [{
      label: 'Revenue Distribution',
      data: [servicesTotal, partsTotal],
      backgroundColor: [
        'rgba(99, 102, 241, 0.8)',
        'rgba(34, 197, 94, 0.8)'
      ],
      borderColor: [
        'rgba(99, 102, 241, 1)',
        'rgba(34, 197, 94, 1)'
      ],
      borderWidth: 2,
    }]
  };

  const topServicesChartData = {
    labels: topServices.map(s => s.name.substring(0, 30)),
    datasets: [{
      label: 'Revenue',
      data: topServices.map(s => s.revenue),
      backgroundColor: 'rgba(99, 102, 241, 0.7)',
      borderColor: 'rgba(99, 102, 241, 1)',
      borderWidth: 2,
    }]
  };

  const topPartsChartData = {
    labels: topParts.map(p => p.name.substring(0, 30)),
    datasets: [{
      label: 'Revenue',
      data: topParts.map(p => p.revenue),
      backgroundColor: 'rgba(34, 197, 94, 0.7)',
      borderColor: 'rgba(34, 197, 94, 1)',
      borderWidth: 2,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        callbacks: {
          label: function(context) {
            return `Revenue: ${formatCurrency(context.parsed.y || context.parsed)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          font: { size: 14, weight: 'bold' }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const percentage = ((value / totalRevenue) * 100).toFixed(1);
            return `${label}: ${formatCurrency(value)} (${percentage}%)`;
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="text-center">
          <MoonLoader size={60} color="#6366F1" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Finance' && role !== 'Admin' && role !== 'Service Advisor') {
    return <Navigate to="/unauthorized" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="text-center">
          <MoonLoader size={60} color="#6366F1" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading revenue analytics...</p>
        </div>
      </div>
    );
  }

  const servicesPercentage = totalRevenue > 0 ? ((servicesTotal / totalRevenue) * 100).toFixed(1) : 0;
  const partsPercentage = totalRevenue > 0 ? ((partsTotal / totalRevenue) * 100).toFixed(1) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50 py-4">
      <BreadCrumb pages={pages} />
      <ToastContainer />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ChartPieIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Revenue by Service Type</h1>
              <p className="text-sm text-gray-600 mt-1">Comprehensive revenue breakdown and analysis</p>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-l-4 border-indigo-500 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-indigo-600 uppercase">Total Revenue</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(totalRevenue)}</p>
              </div>
              <CurrencyDollarIcon className="w-12 h-12 text-indigo-500 opacity-50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-l-4 border-emerald-500 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-emerald-600 uppercase">Services Revenue</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(servicesTotal)}</p>
                <p className="text-xs text-emerald-600 mt-1 font-semibold">{servicesPercentage}% of total</p>
              </div>
              <WrenchScrewdriverIcon className="w-12 h-12 text-emerald-500 opacity-50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-l-4 border-blue-500 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase">Parts Revenue</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(partsTotal)}</p>
                <p className="text-xs text-blue-600 mt-1 font-semibold">{partsPercentage}% of total</p>
              </div>
              <ShoppingCartIcon className="w-12 h-12 text-blue-500 opacity-50" />
            </div>
          </div>
        </div>

        {/* Services vs Parts Distribution */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <ChartPieIcon className="w-6 h-6" />
              Services vs Parts Revenue Distribution
            </h3>
            <p className="text-indigo-100 text-xs mt-1">Overall revenue split between services and parts</p>
          </div>
          <div className="p-6 flex justify-center" style={{ height: '400px' }}>
            {totalRevenue > 0 ? (
              <div style={{ maxWidth: '400px', width: '100%' }}>
                <Doughnut data={serviceVsPartsData} options={pieOptions} />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No revenue data available
              </div>
            )}
          </div>
        </div>

        {/* Top Services & Parts Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Services */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <WrenchScrewdriverIcon className="w-6 h-6" />
                Top 10 Services by Revenue
              </h3>
              <p className="text-emerald-100 text-xs mt-1">Highest earning service types</p>
            </div>
            <div className="p-6" style={{ height: '500px' }}>
              {topServices.length > 0 ? (
                <Bar data={topServicesChartData} options={chartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No service data available
                </div>
              )}
            </div>
          </div>

          {/* Top Parts */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-cyan-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <ShoppingCartIcon className="w-6 h-6" />
                Top 10 Parts by Revenue
              </h3>
              <p className="text-blue-100 text-xs mt-1">Highest earning part types</p>
            </div>
            <div className="p-6" style={{ height: '500px' }}>
              {topParts.length > 0 ? (
                <Bar data={topPartsChartData} options={chartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No parts data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Detailed Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Services Table */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
              <h3 className="text-lg font-bold text-white">Services Breakdown</h3>
              <p className="text-emerald-100 text-xs mt-1">Complete list of all services</p>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 uppercase">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 uppercase">Count</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 uppercase">Revenue</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {Object.entries(serviceRevenue).map(([name, data]) => (
                    <tr key={name} className="hover:bg-emerald-50 transition-colors duration-150">
                      <td className="px-4 py-2 text-xs text-gray-900">{name}</td>
                      <td className="px-4 py-2 text-xs text-gray-700">{data.count}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-900">{formatCurrency(data.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Parts Table */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-cyan-600 px-6 py-4">
              <h3 className="text-lg font-bold text-white">Parts Breakdown</h3>
              <p className="text-blue-100 text-xs mt-1">Complete list of all parts</p>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 uppercase">Part</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 uppercase">Count</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 uppercase">Revenue</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {Object.entries(partsRevenue).map(([name, data]) => (
                    <tr key={name} className="hover:bg-blue-50 transition-colors duration-150">
                      <td className="px-4 py-2 text-xs text-gray-900">{name}</td>
                      <td className="px-4 py-2 text-xs text-gray-700">{data.count}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-900">{formatCurrency(data.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueByServiceType;
